import React from 'react';
import {
  Box,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  SelectProps,
  Tooltip,
  Typography,
} from '@mui/material';

import { LocalizationContext } from '@cvt/contexts';
import { getStoryTypeLabel } from '../helpers/story';
import { STORY_TYPES, StoryType, StoryTypeKey, StoryTypeDescriptions } from '../enums';


export type StoryTypeSelectProps = Omit<SelectProps<string | null>, 'renderValue'> & {
  error?: boolean;
  helperText?: React.ReactNode;
  placeholder?: string;
  fullWidth?: boolean;
};

export const StoryTypeSelect = React.forwardRef<HTMLDivElement, StoryTypeSelectProps>((props, ref) => {
  const { error, helperText, placeholder, label, fullWidth, ...selectProps } = props;
  const { dictionary } = React.useContext(LocalizationContext);

  return (
    <Box width={fullWidth ? '100%' : undefined}>
      <FormControl fullWidth={fullWidth}>
        <InputLabel>{label}</InputLabel>
        <Select
          displayEmpty
          label={label}
          {...selectProps}
          ref={ref}
          color="primary"
          renderValue={(selected) => {
            const storyType = STORY_TYPES.find((storyType) => storyType === selected);
            if (!storyType) {
              return (
                <span style={{ color: '#a2a2a2' }}>
                  {placeholder || dictionary.forms.story.storyTypePlaceholder}
                </span>
              );
            }
            return (
              <Typography>
                {getStoryTypeLabel(dictionary, storyType)}
              </Typography>
            );
          }}
          MenuProps={{
            ...selectProps.MenuProps,
            PaperProps: {
              ...selectProps.MenuProps?.PaperProps,
              sx: {
                maxHeight: (36 * 8.5) + 8,
              },
            },
          }}
          error={error}
        >
          <MenuItem value="">
            <span style={{ color: '#a2a2a2' }}>
              {placeholder || dictionary.forms.story.storyTypePlaceholder}
            </span>
          </MenuItem>
          {STORY_TYPES.map((storyType) => {
            const storyTypeKey = Object.keys(StoryType).find((key) => StoryType[key as StoryTypeKey] === storyType) as StoryTypeKey;

            return (
              <MenuItem value={storyType} key={storyType} sx={{ textTransform: 'capitalize' }}>
                <Tooltip title={StoryTypeDescriptions[storyTypeKey]}>
                  <span>{getStoryTypeLabel(dictionary, storyType)}</span>
                </Tooltip>
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
      {!!helperText && (
        <FormHelperText error={error}>{helperText}</FormHelperText>
      )}
    </Box>
  );
});
